import { TCasePif1 } from "../codecs/CasePif";
import * as regexes from "../../shared/src/regexes";
import * as E164PhoneNumber from "../../shared/src/util/E164PhoneNumber";

// Go-live cutoff for the new internal PIF flow. A case only adopts the new
// flow (seller PIF step + pif_received roadblock that supersedes TA6/TA7/TA10)
// when it was created on or after this instant — so enabling an introducer's
// pif_enabled flag does not retroactively pull its in-flight legacy cases onto
// the new flow mid-process. Gates both is_on_new_pif_flow and
// client_should_complete_pif in lockstep. Mirrors ILA_WAIVER_GO_LIVE.
export const PIF_GO_LIVE = "2026-06-15T00:00:00.000Z";

export const isOnBaspiFlow = (c: {
    use_baspi: boolean | null,
    created_at: string,
    client_says_pifs_submitted: boolean,
}): boolean =>
    c.use_baspi !== null
        ? c.use_baspi
        : c.created_at >= PIF_GO_LIVE || c.client_says_pifs_submitted;

export type TPifFieldKey = keyof TCasePif1;

export type TPifFieldType = "boolean" | "select" | "integer" | "decimal" | "text" | "date" | "email" | "phone";

export type TPifTitleMode = "full" | "limited";

export type TPifLimitedTitleBehavior =
    | { kind: "show" }
    | { kind: "auto_answer", canonicalText?: string }
    | { kind: "auto_default", value: boolean | string | number };

export const PIF_DONT_KNOW_VALUE = "__pif_dont_know__";

export type TPifField = {
    key: TPifFieldKey,
    label: string,
    type: TPifFieldType,
    required?: boolean,
    allowsDontKnow?: true,
    helpText?: string,
    placeholder?: string,
    options?: { value: string, label: string }[],
    showWhen?: { field: TPifFieldKey | TPifFieldKey[], value: string | boolean | Array<string | boolean> },
    freeTextWhen?: { field: TPifFieldKey, value: string | boolean },
    freeTextPrompt?: string,
};

export const getPifFreeTextKey = (field: TPifField): string => `${field.key}__details`;

export const getPifFreeTextValue = (
    freeText: { field_key: string, value: string }[],
    key: string,
): string => freeText.find((f) => f.field_key === key)?.value ?? "";

export const shouldShowFreeText = (
    field: TPifField,
    data: TCasePif1,
): boolean => {
    if (!field.freeTextWhen) return false;
    return data[field.freeTextWhen.field] === field.freeTextWhen.value;
};

export type TPifSection = {
    key: string,
    title: string,
    description: string,
    leaseholdOnly?: boolean,
    fields: TPifField[],
};

const propertyType = (): { value: string, label: string }[] => [
    { value: "detached", label: "Detached House" },
    { value: "semi_detached", label: "Semi-Detached House" },
    { value: "terraced", label: "Terraced House" },
    { value: "flat", label: "Flat / Apartment" },
    { value: "maisonette", label: "Maisonette" },
    { value: "bungalow", label: "Bungalow" },
    { value: "cottage", label: "Cottage" },
    { value: "other", label: "Other" },
];

const yesNoUnknown = (): { value: string, label: string }[] => [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "unknown", label: "Unknown" },
];

const inclusion = (includeNone: boolean): { value: string, label: string }[] => [
    { value: "included", label: "Included in sale" },
    { value: "excluded", label: "Excluded from sale" },
    ...(includeNone ? [{ value: "none", label: "None at property" }] : []),
];

const boundaryOwner = (): { value: string, label: string }[] => [
    { value: "seller", label: "Seller" },
    { value: "neighbour", label: "Neighbour" },
    { value: "shared", label: "Shared" },
    { value: "unknown", label: "Unknown" },
];

const tenureType = (): { value: string, label: string }[] => [
    { value: "freehold", label: "Freehold" },
    { value: "managed_freehold", label: "Managed freehold" },
    { value: "leasehold", label: "Leasehold" },
    { value: "commonhold", label: "Commonhold" },
    { value: "shared_ownership", label: "Shared ownership" },
    { value: "other", label: "Other" },
];

const LEASE_TENURES = ["leasehold", "shared_ownership"];
const MANAGED_TENURES = ["leasehold", "shared_ownership", "commonhold", "managed_freehold"];

const rentFrequency = (): { value: string, label: string }[] => [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" },
];

const yesNoNa = (): { value: string, label: string }[] => [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "not_applicable", label: "Not applicable" },
];

const consentStatus = (): { value: string, label: string }[] => [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "not_required", label: "Not required" },
];

const councilTaxBand = (): { value: string, label: string }[] => [
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
    { value: "F", label: "F" },
    { value: "G", label: "G" },
    { value: "H", label: "H" },
    { value: "I_wales_only", label: "I (Wales only)" },
    { value: "exempt", label: "Exempt" },
];

export const pifSections: TPifSection[] = [
    {
        key: "property_details",
        title: "Property Details",
        description: "A few extra details about the property that aren't in our records yet.",
        fields: [
            { key: "property_type", label: "Property Type", type: "select", required: true, options: propertyType() },
            { key: "property_type_other", label: "Please specify property type", type: "text", required: true,
                showWhen: { field: "property_type", value: "other" } },
            // UPRN is deliberately NOT surfaced to the seller — no seller knows
            // it off-hand and nothing prefills it. Same call as the EPC
            // certificate number and expiry below. The cases_pif column stays.
            { key: "num_bedrooms", label: "Number of Bedrooms", type: "integer", required: true },
            { key: "num_bathrooms", label: "Number of Bathrooms", type: "integer",
                helpText: "Include en-suites and shower rooms." },
            { key: "num_stories", label: "Number of Stories", type: "integer" },
            { key: "year_built", label: "Approximate Year Built", type: "text", placeholder: "e.g. 1920",
                allowsDontKnow: true },
            { key: "has_commercial", label: "Does the property include any commercial use?", type: "boolean" },
            { key: "step_free_street", label: "Is there step-free access from the street?", type: "boolean" },
            { key: "step_free_throughout", label: "Is there step-free access throughout the property?", type: "boolean" },
            { key: "has_sale_delay_factors", label: "Any factors that might delay or complicate the sale?", type: "boolean",
                helpText: "E.g. family split, pending grant of probate, absent seller, unregistered title.",
                freeTextWhen: { field: "has_sale_delay_factors", value: true } },
        ],
    },
    {
        key: "tenure_lease",
        title: "Tenure & Lease",
        description: "How you own the property, and (for leasehold, commonhold, shared ownership and managed freehold) the running costs, freeholder and managing agent.",
        fields: [
            { key: "tenure_type", label: "Tenure", type: "select", options: tenureType(),
                allowsDontKnow: true,
                helpText: "Freehold means you own the building and the land it sits on. Leasehold means you own it for a set number of years. It's on your title deeds and the estate agent's listing." },
            { key: "shared_ownership_share_percentage", label: "Percentage of the share being sold (%)", type: "integer",
                placeholder: "e.g. 50",
                showWhen: { field: "tenure_type", value: "shared_ownership" } },
            { key: "shared_ownership_rent_full_currency", label: "Shared ownership rent (£)", type: "decimal",
                showWhen: { field: "tenure_type", value: "shared_ownership" } },
            { key: "shared_ownership_rent_frequency", label: "Frequency of shared ownership rent", type: "select",
                options: rentFrequency(),
                showWhen: { field: "tenure_type", value: "shared_ownership" } },
            { key: "lease_start_date", label: "Lease start date", type: "date",
                allowsDontKnow: true,
                showWhen: { field: "tenure_type", value: LEASE_TENURES } },
            { key: "has_ground_rent_increase", label: "Does the ground rent increase?", type: "boolean",
                helpText: "If yes, please describe how often, by how much, and when the next increase is due.",
                showWhen: { field: "tenure_type", value: LEASE_TENURES },
                freeTextWhen: { field: "has_ground_rent_increase", value: true } },
            { key: "has_reserve_fund", label: "Is there a reserve fund (also known as sinking fund)?", type: "boolean",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "reserve_fund_included_in_service_charge", label: "Is the annual reserve fund contribution included in the service charge figure?",
                type: "boolean",
                showWhen: { field: "has_reserve_fund", value: true } },
            { key: "has_large_expense_notice", label: "Have you received notice of any large expense in addition to the annual payment?",
                type: "boolean",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES },
                freeTextWhen: { field: "has_large_expense_notice", value: true } },
            { key: "additional_fees_on_sale_or_letting", label: "Additional fees payable on sale or letting (if known)", type: "text",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "freeholder_name", label: "Freeholder / rentcharge owner name", type: "text",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "freeholder_address", label: "Freeholder / rentcharge owner address", type: "text",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "freeholder_email", label: "Freeholder / rentcharge owner email", type: "email",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "managing_agent_name", label: "Managing agent name", type: "text",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "managing_agent_address", label: "Managing agent address", type: "text",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "managing_agent_email", label: "Managing agent email", type: "email",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
            { key: "owner_required_to_be_director", label: "Is the owner required to be a director of the management company?",
                type: "boolean",
                showWhen: { field: "tenure_type", value: MANAGED_TENURES } },
        ],
    },
    {
        key: "parking_ev",
        title: "Parking & EV Charging",
        description: "Tick every parking arrangement that applies to the property.",
        fields: [
            { key: "parking_garage", label: "Garage", type: "boolean" },
            { key: "parking_allocated", label: "Allocated parking space", type: "boolean" },
            { key: "parking_driveway", label: "Driveway", type: "boolean" },
            { key: "parking_on_street", label: "On-street parking", type: "boolean" },
            { key: "parking_resident_permit", label: "Resident permit", type: "boolean" },
            { key: "parking_permit_cost_full_currency", label: "Resident permit cost, if any (£)", type: "decimal",
                showWhen: { field: "parking_resident_permit", value: true } },
            { key: "parking_metered", label: "Metered parking", type: "boolean" },
            { key: "parking_unallocated_communal", label: "Unallocated communal parking", type: "boolean" },
            { key: "parking_controlled_zone", label: "In a Controlled Parking Zone", type: "boolean" },
            { key: "parking_none", label: "No parking at the property", type: "boolean" },
            { key: "parking_other", label: "Other parking arrangement", type: "boolean",
                freeTextWhen: { field: "parking_other", value: true } },
            { key: "parking_vehicle_restriction", label: "Vehicle-type restrictions (if any)", type: "text",
                helpText: "Describe the types of vehicle restricted, e.g. no caravans or commercial vehicles." },
            { key: "has_ev_charge_point", label: "Is there an Electric Vehicle charge point at the property?", type: "boolean" },
            { key: "has_ev_charge_points_nearby", label: "Are there electric charging points nearby?", type: "boolean",
                showWhen: { field: "has_ev_charge_point", value: false } },
        ],
    },
    {
        key: "disputes_complaints",
        title: "Disputes & Complaints",
        description: "Buyers need to know about any disagreements or problems with neighbours or the local area.",
        fields: [
            { key: "has_disputes_or_complaints", label: "Have there been any disputes or complaints relating to the property — boundary disagreements, noise, trespass, anti-social behaviour, neighbour disputes, or any legal action?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_disputes_or_complaints", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_neighbour_disputes", label: "Any disputes or complaints with neighbours?", type: "boolean", required: true,
                showWhen: { field: "has_disputes_or_complaints", value: true },
                freeTextWhen: { field: "has_neighbour_disputes", value: true } },
            { key: "has_legal_action", label: "Any legal action against the property or by the owner?", type: "boolean", required: true,
                showWhen: { field: "has_disputes_or_complaints", value: true },
                freeTextWhen: { field: "has_legal_action", value: true } },
            { key: "has_antisocial_behaviour", label: "Any anti-social behaviour in the area?", type: "boolean", required: true,
                showWhen: { field: "has_disputes_or_complaints", value: true },
                freeTextWhen: { field: "has_antisocial_behaviour", value: true } },
            { key: "has_noise_issues", label: "Any noise issues affecting the property?", type: "boolean",
                showWhen: { field: "has_disputes_or_complaints", value: true },
                freeTextWhen: { field: "has_noise_issues", value: true } },
        ],
    },
    {
        key: "alterations",
        title: "Alterations & Changes",
        description: "Tell us about any building work or changes made to the property.",
        fields: [
            { key: "has_extensions", label: "Any extensions added to the property?", type: "boolean", required: true,
                freeTextWhen: { field: "has_extensions", value: true } },
            { key: "extension_approved", label: "Was planning permission and/or building regs obtained for the extension?",
                type: "select", showWhen: { field: "has_extensions", value: true },
                options: [
                    { value: "yes_both", label: "Yes - both obtained" },
                    { value: "planning_only", label: "Planning permission only" },
                    { value: "building_regs_only", label: "Building regulations only" },
                    { value: "neither", label: "Neither obtained" },
                    { value: "not_required", label: "Not required (permitted development)" },
                    { value: "unknown", label: "Unknown" },
                ] },
            { key: "has_loft_conversion", label: "Has a loft conversion been carried out?", type: "boolean", required: true,
                freeTextWhen: { field: "has_loft_conversion", value: true } },
            { key: "has_walls_removed", label: "Have any internal walls been removed?", type: "boolean", required: true,
                freeTextWhen: { field: "has_walls_removed", value: true } },
            { key: "has_other_structural", label: "Any other structural changes?", type: "boolean",
                freeTextWhen: { field: "has_other_structural", value: true } },
            { key: "has_significant_repairs", label: "Any significant repairs or renewals?", type: "boolean",
                freeTextWhen: { field: "has_significant_repairs", value: true } },
            { key: "has_change_of_use", label: "Has there been any change of use?", type: "boolean",
                freeTextWhen: { field: "has_change_of_use", value: true } },
            { key: "has_window_replacement", label: "Have any windows, roof windows, roof lights or glazed doors been replaced since 1 April 2002?",
                type: "boolean",
                helpText: "FENSA or CERTASS certificates are usually required for window replacements after this date.",
                freeTextWhen: { field: "has_window_replacement", value: true } },
            { key: "has_conservatory_added", label: "Has a conservatory been added?", type: "boolean",
                helpText: "If yes, please describe the year, whether any walls were removed, and any later roof changes.",
                freeTextWhen: { field: "has_conservatory_added", value: true } },
            { key: "has_alteration_consents_required", label: "For any alterations or extensions you've made, do consents need to be confirmed (planning permission, building regs, listed-building consent, deed restriction consent)?",
                type: "boolean", required: true,
                showWhen: {
                    field: [
                        "has_extensions",
                        "has_loft_conversion",
                        "has_walls_removed",
                        "has_other_structural",
                        "has_significant_repairs",
                        "has_change_of_use",
                        "has_window_replacement",
                        "has_conservatory_added",
                    ],
                    value: true,
                },
                freeTextWhen: { field: "has_alteration_consents_required", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "alteration_building_regs_approval", label: "For each alteration above: was Building Regulations approval (or Competent Person certificate) obtained?",
                type: "select", options: consentStatus(),
                showWhen: { field: "has_alteration_consents_required", value: true } },
            { key: "alteration_planning_permission", label: "For each alteration above: was planning permission obtained?",
                type: "select", options: consentStatus(),
                showWhen: { field: "has_alteration_consents_required", value: true } },
            { key: "alteration_listed_or_conservation_consent", label: "For each alteration above: was listed building or conservation area consent obtained?",
                type: "select", options: consentStatus(),
                showWhen: { field: "has_alteration_consents_required", value: true } },
            { key: "alteration_deed_restriction_consent", label: "For each alteration above: was any consent required by the deeds obtained?",
                type: "select", options: consentStatus(),
                showWhen: { field: "has_alteration_consents_required", value: true } },
            { key: "alteration_works_unfinished", label: "Are any of the works above unfinished?", type: "boolean",
                freeTextWhen: { field: "alteration_works_unfinished", value: true } },
            { key: "has_planning_breach", label: "Any known breach of planning permission or building regulations, or work without the necessary consents?",
                type: "boolean",
                freeTextWhen: { field: "has_planning_breach", value: true } },
            { key: "has_unresolved_building_control", label: "Any unresolved planning or building control issues?", type: "boolean",
                freeTextWhen: { field: "has_unresolved_building_control", value: true } },
            { key: "will_pay_for_indemnity_for_breach", label: "Will you pay for indemnity insurance to cover any breach above?",
                type: "boolean",
                helpText: "Only relevant if you have no title defect policy and the local authority can still enforce.",
                freeTextWhen: { field: "will_pay_for_indemnity_for_breach", value: false } },
        ],
    },
    {
        key: "notices",
        title: "Notices",
        description: "Any official letters or notices about the property from the council or other authorities.",
        fields: [
            { key: "has_received_notices", label: "Are you aware of, or have you received, any notices that affect the property?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_received_notices", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_planning_notices", label: "Any planning notices affecting the property or area?", type: "boolean", required: true,
                showWhen: { field: "has_received_notices", value: true },
                freeTextWhen: { field: "has_planning_notices", value: true } },
            { key: "has_building_control_notices", label: "Any building control notices?", type: "boolean", required: true,
                showWhen: { field: "has_received_notices", value: true },
                freeTextWhen: { field: "has_building_control_notices", value: true } },
            { key: "has_environmental_notices", label: "Any environmental notices (e.g. contamination, flooding)?", type: "boolean", required: true,
                showWhen: { field: "has_received_notices", value: true },
                freeTextWhen: { field: "has_environmental_notices", value: true } },
            { key: "has_notice_neighbour_development", label: "Notice that a neighbour proposes to develop nearby property or land?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true } },
            { key: "has_notice_planning_application", label: "Notice of a planning application that could affect the property or its views?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true } },
            { key: "has_notice_required_maintenance", label: "Notice that maintenance, repairs or improvements are required to your property?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true } },
            { key: "has_notice_listed_building_application", label: "Notice of a listed building application?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true } },
            { key: "has_notice_infrastructure_project", label: "Notice of an infrastructure project (wind turbine, railway, fracking, etc.)?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true },
                freeTextWhen: { field: "has_notice_infrastructure_project", value: true } },
            { key: "has_notice_party_wall", label: "Notice under the Party Wall etc. Act 1996 for shared or party boundaries?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true } },
            { key: "has_other_notices", label: "Any other notices or proposals affecting the property?", type: "boolean",
                showWhen: { field: "has_received_notices", value: true },
                freeTextWhen: { field: "has_other_notices", value: true } },
        ],
    },
    {
        key: "specialist_issues",
        title: "Specialist Issues",
        description: "Specific problems that can significantly affect a property's value or safety.",
        fields: [
            { key: "has_specialist_issues", label: "Are you aware of any specialist issues at the property — asbestos, Japanese knotweed, damp, rot, subsidence, structural fault, or a health/safety notice from an authority?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_specialist_issues", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_japanese_knotweed", label: "Japanese Knotweed at the property?", type: "boolean", required: true,
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_japanese_knotweed", value: true } },
            { key: "has_flooding", label: "Has the property ever been flooded?", type: "boolean", required: true,
                showWhen: { field: "has_environmental_issues", value: true },
                freeTextWhen: { field: "has_flooding", value: true } },
            { key: "has_subsidence", label: "Affected by subsidence, heave or landslip?", type: "boolean", required: true,
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_subsidence", value: true } },
            { key: "has_asbestos", label: "Any known asbestos?", type: "boolean", required: true,
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_asbestos", value: true } },
            { key: "has_radon", label: "Is the property in a radon-affected area?", type: "select", options: yesNoUnknown(),
                showWhen: { field: "has_environmental_issues", value: true },
                freeTextWhen: { field: "has_radon", value: "yes" } },
            { key: "has_coastal_erosion", label: "At risk of coastal erosion?", type: "boolean",
                showWhen: { field: "has_environmental_issues", value: true },
                freeTextWhen: { field: "has_coastal_erosion", value: true } },
            { key: "has_mining", label: "In a mining area?", type: "select", options: yesNoUnknown(),
                showWhen: { field: "has_environmental_issues", value: true },
                freeTextWhen: { field: "has_mining", value: "yes" } },
            { key: "has_listed_building", label: "Is the property a listed building?", type: "boolean",
                freeTextWhen: { field: "has_listed_building", value: true } },
            { key: "has_conservation_area", label: "Is the property in a designated conservation area?", type: "boolean" },
            { key: "has_tree_preservation_order", label: "Does a Tree Preservation Order apply to any trees on the property?",
                type: "boolean" },
            { key: "tree_work_consents_obtained", label: "If tree work has been carried out, were the relevant consents obtained?",
                type: "select", options: yesNoNa(),
                showWhen: { field: "has_tree_preservation_order", value: true } },
            { key: "has_authority_notified_safety", label: "Have you been notified by an authority or qualified expert of an ongoing health or safety issue with the property?",
                type: "boolean",
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_authority_notified_safety", value: true } },
        ],
    },
    {
        key: "construction_safety",
        title: "Construction & Building Safety",
        description: "How the property is built, and any building-safety or energy-retrofit issues a buyer's surveyor or mortgage lender will look for.",
        fields: [
            { key: "is_standard_construction", label: "Is the property built in a standard form of construction (brick / block with timber-supported tile or slate roof)?",
                type: "boolean",
                freeTextWhen: { field: "is_standard_construction", value: false } },
            { key: "has_flat_roof", label: "Is there a flat roof anywhere at the property?", type: "boolean" },
            { key: "flat_roof_percentage", label: "Approximate percentage of the roof which is flat (%)", type: "integer",
                showWhen: { field: "has_flat_roof", value: true } },
            { key: "flat_roof_last_renewed_year", label: "Year the flat roof was last renewed", type: "text",
                placeholder: "e.g. 2018",
                showWhen: { field: "has_flat_roof", value: true } },
            { key: "has_building_safety_remediation_required", label: "Are you aware of any remediation required to the property for building-safety reasons?",
                type: "boolean",
                helpText: "E.g. high building height, work completed without building regulations, cladding issues.",
                freeTextWhen: { field: "has_building_safety_remediation_required", value: true } },
            { key: "has_energy_retrofit", label: "Has the property been retrofitted to improve its energy efficiency (cavity wall, roof void, internal or external insulation)?",
                type: "boolean",
                freeTextWhen: { field: "has_energy_retrofit", value: true } },
            { key: "has_spray_foam", label: "Has spray foam insulation been installed at the property?", type: "boolean",
                helpText: "Many lenders refuse mortgages on properties with spray foam, so please disclose even if removed.",
                freeTextWhen: { field: "has_spray_foam", value: true } },
            { key: "is_above_two_storeys", label: "Is the property located above 2 storeys (excluding basements)?", type: "boolean" },
            { key: "building_storey_count", label: "How many storeys in the entire building above ground?", type: "integer",
                showWhen: { field: "is_above_two_storeys", value: true } },
            { key: "property_storey_location", label: "On which storey(s) is the property located?", type: "text",
                placeholder: "e.g. 4th floor",
                showWhen: { field: "is_above_two_storeys", value: true } },
            { key: "is_mixed_use_building", label: "Is the property in a mixed-use building (shops, offices etc.)?", type: "boolean",
                freeTextWhen: { field: "is_mixed_use_building", value: true } },
        ],
    },
    {
        key: "council_tax",
        title: "Council Tax",
        description: "Which Council Tax band the property falls within, and whether any alterations may have changed it.",
        fields: [
            { key: "council_tax_band", label: "Council Tax band", type: "select", options: councilTaxBand(),
                allowsDontKnow: true },
            { key: "has_altered_for_council_tax_band", label: "Have you altered the property in a way that might change the Council Tax band, or left it vacant long enough for the Empty Property Premium to apply?",
                type: "boolean",
                freeTextWhen: { field: "has_altered_for_council_tax_band", value: true } },
        ],
    },
    {
        key: "fixtures_fittings",
        title: "Fixtures & Fittings",
        description: "What stays and what goes? For each item, tell us whether it's included in the sale.",
        fields: [
            { key: "kitchen_fitted_units", label: "Fitted kitchen units", type: "select", required: true, options: inclusion(true), allowsDontKnow: true },
            { key: "kitchen_appliances", label: "Kitchen appliances (oven, hob, extractor)", type: "select", options: inclusion(true), allowsDontKnow: true },
            { key: "light_fittings", label: "Light fittings", type: "select", options: inclusion(false), allowsDontKnow: true },
            { key: "curtains_blinds", label: "Curtains and blinds", type: "select", options: inclusion(true), allowsDontKnow: true },
            { key: "carpets", label: "Fitted carpets / floor coverings", type: "select", options: inclusion(true), allowsDontKnow: true },
            { key: "bathroom_fittings", label: "Bathroom fittings", type: "select", options: inclusion(false), allowsDontKnow: true },
            { key: "garden_shed_greenhouse", label: "Garden shed / greenhouse / outbuildings", type: "select", options: inclusion(true), allowsDontKnow: true },
            { key: "satellite_dish_aerial", label: "TV aerial / satellite dish", type: "select", options: inclusion(true), allowsDontKnow: true },
            { key: "additional_furniture_to_include", label: "Additional furniture or possessions you are very likely to include in the sale",
                type: "text",
                helpText: "Helps a buyer's offer take into account what is staying. A full list will be completed after sale agreed." },
        ],
    },
    {
        key: "utilities_services",
        title: "Utilities & Services",
        description: "Gas, electric, water, heating, and broadband at the property.",
        fields: [
            { key: "electricity_supplier", label: "Electricity supplier", type: "text" },
            { key: "gas_connected", label: "Is mains gas connected?", type: "boolean", required: true },
            { key: "gas_supplier", label: "Gas supplier", type: "text",
                showWhen: { field: "gas_connected", value: true } },
            { key: "lpg_connected", label: "Is Liquid Petroleum Gas (LPG) connected?", type: "boolean" },
            { key: "lpg_supplier", label: "LPG supplier", type: "text",
                showWhen: { field: "lpg_connected", value: true } },
            { key: "oil_connected", label: "Is oil connected?", type: "boolean" },
            { key: "oil_supplier", label: "Oil supplier", type: "text",
                showWhen: { field: "oil_connected", value: true } },
            { key: "has_cable_tv_satellite", label: "Is cable TV or satellite connected?", type: "boolean" },
            { key: "has_sustainable_drainage_system", label: "Is a Sustainable Drainage System (SuDS) in use at the property?", type: "boolean" },
            { key: "water_supplier", label: "Water supplier", type: "text" },
            { key: "water_meter", label: "Is there a water meter?", type: "boolean" },
            { key: "sewerage_connection", label: "How is the property connected to sewerage?", type: "select", required: true,
                options: [
                    { value: "mains", label: "Mains sewer" },
                    { value: "septic_tank", label: "Septic tank" },
                    { value: "cesspit", label: "Cesspit" },
                    { value: "treatment_plant", label: "Treatment plant" },
                    { value: "other", label: "Other" },
                ] },
            { key: "sewerage_other", label: "Please describe", type: "text",
                showWhen: { field: "sewerage_connection", value: "other" } },
            { key: "has_septic_tank", label: "Is there a septic tank at the property?", type: "boolean" },
            { key: "septic_tank_last_emptied_date", label: "Septic tank last emptied date", type: "date",
                showWhen: { field: "has_septic_tank", value: true } },
            { key: "has_cesspit", label: "Is there a cesspit at the property?", type: "boolean" },
            { key: "cesspit_last_emptied_date", label: "Cesspit last emptied date", type: "date",
                showWhen: { field: "has_cesspit", value: true } },
            { key: "has_sewage_treatment_plant", label: "Is there a sewage treatment plant at the property?", type: "boolean" },
            { key: "sewage_treatment_plant_install_date", label: "Sewage treatment plant install date", type: "date",
                showWhen: { field: "has_sewage_treatment_plant", value: true } },
            { key: "sewage_treatment_plant_last_serviced_date", label: "Sewage treatment plant last serviced date", type: "date",
                showWhen: { field: "has_sewage_treatment_plant", value: true } },
            { key: "sewage_treatment_plant_building_regs_approved", label: "If installed after January 1991, was Building Regulations approval obtained?",
                type: "boolean",
                showWhen: { field: "has_sewage_treatment_plant", value: true } },
            { key: "off_mains_other_properties_connect", label: "Do other properties connect to the septic tank, cesspit or treatment plant?",
                type: "boolean",
                freeTextWhen: { field: "off_mains_other_properties_connect", value: true } },
            { key: "off_mains_off_site", label: "Is any part of the off-mains system located on someone else's land?",
                type: "boolean",
                freeTextWhen: { field: "off_mains_off_site", value: true } },
            { key: "off_mains_environment_agency_registered", label: "Is the system registered with the Environment Agency, or exempt?",
                type: "select", options: yesNoNa() },
            { key: "off_mains_drains_into_waterway", label: "Does the system drain into a waterway (lake, river, stream)?",
                type: "boolean" },
            { key: "off_mains_complies_with_general_binding_rules", label: "Does it comply with the General Binding Rules for discharge into a waterway?",
                type: "boolean",
                showWhen: { field: "off_mains_drains_into_waterway", value: true } },
            { key: "has_utility_licences_or_maintenance_agreements", label: "Do you have any licences, maintenance agreements or service contracts for the utility installations above?",
                type: "boolean",
                freeTextWhen: { field: "has_utility_licences_or_maintenance_agreements", value: true } },
            { key: "heating_type", label: "Type of central heating", type: "select", required: true,
                options: [
                    { value: "gas_central", label: "Gas central heating" },
                    { value: "oil", label: "Oil-fired" },
                    { value: "electric", label: "Electric" },
                    { value: "heat_pump_air", label: "Air source heat pump" },
                    { value: "heat_pump_ground", label: "Ground source heat pump" },
                    { value: "heat_pump_water", label: "Water source heat pump" },
                    { value: "solid_fuel", label: "Solid fuel" },
                    { value: "none", label: "None" },
                    { value: "other", label: "Other" },
                ] },
            { key: "heating_other", label: "Please describe the heating", type: "text",
                showWhen: { field: "heating_type", value: "other" } },
            { key: "boiler_age", label: "Approximate age of boiler (years)", type: "integer" },
            { key: "heating_install_date", label: "When was the heating system installed?", type: "date" },
            { key: "heating_last_serviced_date", label: "When was the heating system last serviced or maintained?", type: "date" },
            { key: "heating_in_good_working_order", label: "Is the heating system in good working order to your satisfaction?", type: "boolean",
                freeTextWhen: { field: "heating_in_good_working_order", value: false } },
            { key: "broadband_type", label: "Broadband availability", type: "select",
                options: [
                    { value: "fibre_full", label: "Full fibre (FTTP)" },
                    { value: "fibre_cabinet", label: "Fibre to cabinet (FTTC)" },
                    { value: "adsl", label: "ADSL" },
                    { value: "cable", label: "Cable" },
                    { value: "none", label: "No broadband" },
                    { value: "unknown", label: "Unknown" },
                ] },
            { key: "mobile_signal", label: "Mobile phone signal coverage", type: "select",
                options: [
                    { value: "good", label: "Good" },
                    { value: "moderate", label: "Moderate" },
                    { value: "poor", label: "Poor" },
                    { value: "none", label: "No coverage" },
                    { value: "unknown", label: "Unknown" },
                ] },
            { key: "solar_panels", label: "Are there solar panels on the property?", type: "boolean" },
            { key: "in_ulez", label: "Is the property within the ULEZ zone? (London only)", type: "select",
                options: [
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                    { value: "not_applicable", label: "Not in London" },
                ] },
        ],
    },
    {
        key: "insurance",
        title: "Insurance",
        description: "Buildings insurance — about the building itself, not contents.",
        fields: [
            { key: "is_property_insured", label: "Do you insure the property?", type: "boolean",
                freeTextWhen: { field: "is_property_insured", value: false },
                freeTextPrompt: "If no, please tell us why not." },
            { key: "buildings_insurance_provider", label: "Buildings insurance provider", type: "text" },
            { key: "insurance_policy_number", label: "Policy number", type: "text" },
            { key: "has_insurance_claims", label: "Any insurance claims made in the last 5 years?", type: "boolean", required: true,
                freeTextWhen: { field: "has_insurance_claims", value: true } },
            { key: "has_insurance_refused", label: "Has insurance ever been refused, cancelled, or had special terms?", type: "boolean", required: true,
                freeTextWhen: { field: "has_insurance_refused", value: true } },
            { key: "has_title_indemnity", label: "Any title defect insurance in place?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
        ],
    },
    {
        key: "boundaries",
        title: "Boundaries",
        description: "Who looks after each boundary of your property.",
        fields: [
            { key: "boundary_front", label: "Who owns/maintains the front boundary?", type: "select", required: true, options: boundaryOwner() },
            { key: "boundary_rear", label: "Who owns/maintains the rear boundary?", type: "select", required: true, options: boundaryOwner() },
            { key: "boundary_left", label: "Who owns/maintains the left boundary?", type: "select", required: true, options: boundaryOwner(),
                helpText: "Left side when facing the property from the street." },
            { key: "boundary_right", label: "Who owns/maintains the right boundary?", type: "select", required: true, options: boundaryOwner(),
                helpText: "Right side when facing the property from the street." },
            { key: "has_boundary_issues", label: "Are there any boundary issues — disputes, recent changes, or boundaries that don't match the title plan?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_boundary_issues", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_boundary_disputes", label: "Any boundary disputes?", type: "boolean", required: true,
                showWhen: { field: "has_boundary_issues", value: true },
                freeTextWhen: { field: "has_boundary_disputes", value: true } },
            { key: "boundary_agreements", label: "Any boundary agreements in place?", type: "boolean" },
            { key: "has_part_outside_legal_ownership", label: "Is any part of the property (parking, garden, outbuilding, accessway) outside your legal ownership?",
                type: "boolean",
                showWhen: { field: "has_boundary_issues", value: true },
                freeTextWhen: { field: "has_part_outside_legal_ownership", value: true } },
            { key: "has_part_on_separate_title", label: "Is any part of the property on a separate site or title number?", type: "boolean",
                showWhen: { field: "has_boundary_issues", value: true },
                freeTextWhen: { field: "has_part_on_separate_title", value: true } },
        ],
    },
    {
        key: "rights_arrangements",
        title: "Rights & Informal Arrangements",
        description: "Whether anyone else has the right to use part of your property, or any informal agreements with neighbours.",
        fields: [
            { key: "has_rights_of_way", label: "Any rights of way across the property?", type: "boolean", required: true,
                freeTextWhen: { field: "has_rights_of_way", value: true } },
            { key: "has_shared_access", label: "Is any access shared with others?", type: "boolean", required: true,
                freeTextWhen: { field: "has_shared_access", value: true } },
            { key: "has_informal_arrangements", label: "Any informal arrangements with neighbours?", type: "boolean",
                freeTextWhen: { field: "has_informal_arrangements", value: true } },
            { key: "has_easements", label: "Any easements benefiting or burdening the property?", type: "boolean",
                freeTextWhen: { field: "has_easements", value: true } },
            { key: "has_access_blocked_or_charged", label: "Has anyone tried to stop you using an access way, or asked you to pay for the access?",
                type: "boolean",
                freeTextWhen: { field: "has_access_blocked_or_charged", value: true } },
            { key: "has_other_rights_or_arrangements", label: "Are you aware of any rights, restrictions, or arrangements affecting the property — public rights of way, rights of light or support, customary rights, mines and minerals, chancel liability, or other?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_other_rights_or_arrangements", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_public_right_of_way", label: "Is there a public right of way through or across the property?",
                type: "boolean",
                showWhen: { field: "has_other_rights_or_arrangements", value: true },
                freeTextWhen: { field: "has_public_right_of_way", value: true } },
            { key: "has_rights_of_light", label: "Are there rights of light or support for other properties?", type: "boolean",
                showWhen: { field: "has_other_rights_or_arrangements", value: true } },
            { key: "has_rights_through_custom", label: "Any rights created through custom or use (e.g. grazing, foraging, lord of the manor)?",
                type: "boolean",
                showWhen: { field: "has_other_rights_or_arrangements", value: true },
                freeTextWhen: { field: "has_rights_through_custom", value: true } },
            { key: "has_mines_and_minerals", label: "Are there mines and minerals owned separately from the property?", type: "boolean",
                helpText: "A significant title issue — your conveyancer will want to investigate.",
                showWhen: { field: "has_other_rights_or_arrangements", value: true } },
            { key: "has_chancel_liability", label: "Is the property subject to chancel repair liability?", type: "boolean",
                helpText: "A significant title issue — your conveyancer will want to investigate and may recommend indemnity insurance.",
                showWhen: { field: "has_other_rights_or_arrangements", value: true } },
        ],
    },
    {
        key: "environmental",
        title: "Environmental & Other Issues",
        description: "Final questions about potential environmental problems.",
        fields: [
            { key: "has_environmental_issues", label: "In the last 5 years, are you aware of any environmental or geological issues affecting the property — flooding, radon, mining, sink holes, quarrying, fracking, contamination, or coastal erosion?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_environmental_issues", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_contamination", label: "Any contamination on or near the property?", type: "boolean", required: true,
                showWhen: { field: "has_environmental_issues", value: true },
                freeTextWhen: { field: "has_contamination", value: true } },
            { key: "has_trees", label: "Any trees subject to Tree Preservation Orders?", type: "boolean",
                freeTextWhen: { field: "has_trees", value: true } },
            { key: "has_pest_issues", label: "Any pest infestations (woodworm, dry rot, etc.)?", type: "boolean",
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_pest_issues", value: true } },
            { key: "has_damp_issues", label: "Any damp issues?", type: "boolean",
                showWhen: { field: "has_specialist_issues", value: true },
                freeTextWhen: { field: "has_damp_issues", value: true } },
        ],
    },
    {
        key: "other_issues",
        title: "Other Issues Affecting the Property",
        description: "Material disclosures that buyers and lenders need to know about under Consumer Protection from Unfair Trading Regulations 2008.",
        fields: [
            { key: "has_crime_or_death", label: "To your knowledge, has the property been subject to any crime, burglary or violent death?",
                type: "boolean",
                freeTextWhen: { field: "has_crime_or_death", value: true } },
            { key: "has_failed_transactions", label: "Have there been any failed purchase transactions on the property in the last 12 months?",
                type: "boolean",
                freeTextWhen: { field: "has_failed_transactions", value: true } },
        ],
    },
    {
        key: "additional_information",
        title: "Additional Information",
        description: "The CPR 2008 catch-all: anything else a buyer should reasonably know.",
        fields: [
            { key: "has_other_material_issue", label: "Are you aware of any other material issue or information that may affect the buyer's decision to proceed?",
                type: "boolean",
                helpText: "Required disclosure under the Consumer Protection from Unfair Trading Regulations 2008.",
                freeTextWhen: { field: "has_other_material_issue", value: true } },
        ],
    },
    {
        key: "legal_ownership",
        title: "Legal Ownership",
        description: "About who legally owns the property.",
        fields: [
            { key: "ownership_type", label: "How the owners hold the property", type: "select", required: true,
                allowsDontKnow: true,
                helpText: "Sole owner means one name on the deeds. Joint tenants and tenants in common are two ways of sharing it — if you're not sure which, choose \"I don't know\" and we'll check the title.",
                options: [
                    { value: "sole", label: "Sole owner" },
                    { value: "joint_tenants", label: "Joint tenants" },
                    { value: "tenants_in_common", label: "Tenants in common" },
                ] },
            { key: "all_owners_agree", label: "Do all legal owners agree to the sale?", type: "boolean", required: true },
            { key: "has_restrictions", label: "Any restrictions on the title?", type: "boolean", required: true },
            { key: "has_charges", label: "Any charges registered against the title (mortgage, etc.)?", type: "boolean", required: true },
        ],
    },
    {
        key: "legal_boundaries",
        title: "Legal Boundaries",
        description: "Do the actual fences and walls match the official title plan?",
        fields: [
            { key: "boundaries_match_title", label: "Do physical boundaries match the title plan?", type: "boolean", required: true },
            { key: "has_boundary_changes", label: "Have any boundaries been moved or altered?", type: "boolean", required: true,
                showWhen: { field: "has_boundary_issues", value: true },
                freeTextWhen: { field: "has_boundary_changes", value: true } },
        ],
    },
    {
        key: "services_crossing",
        title: "Services Crossing Other Property",
        description: "Pipes, wires, or drains crossing through a neighbour's land (or vice versa).",
        fields: [
            { key: "has_services_crossing", label: "Do any pipes, wires, cables or drains cross neighbouring property?", type: "boolean", required: true,
                freeTextWhen: { field: "has_services_crossing", value: true } },
            { key: "has_neighbour_services", label: "Do any neighbouring services cross your property?", type: "boolean", required: true,
                freeTextWhen: { field: "has_neighbour_services", value: true } },
            { key: "has_shared_drains", label: "Are any drains shared with neighbouring properties?", type: "boolean",
                freeTextWhen: { field: "has_shared_drains", value: true } },
        ],
    },
    {
        key: "energy",
        title: "Energy",
        description: "Your Energy Performance Certificate (EPC) rating. If you don't know it, you can look it up at epcregister.com, or skip if you haven't got an EPC yet — your conveyancer will follow up.",
        fields: [
            { key: "epc_rating", label: "Current EPC Rating", type: "select",
                allowsDontKnow: true,
                helpText: "Leave blank if you don't have an EPC yet.",
                options: [
                    { value: "A", label: "A" }, { value: "B", label: "B" },
                    { value: "C", label: "C" }, { value: "D", label: "D" },
                    { value: "E", label: "E" }, { value: "F", label: "F" },
                    { value: "G", label: "G" }, { value: "exempt", label: "Exempt" },
                ] },
            { key: "has_green_deal", label: "Is there a Green Deal plan on the property?", type: "boolean" },
        ],
    },
    {
        key: "guarantees_warranties",
        title: "Guarantees & Warranties",
        description: "If any work has been done, there may be guarantees or warranties to pass on.",
        fields: [
            { key: "has_guarantees_or_warranties", label: "Do you have any guarantees, warranties, or indemnity insurance policies for the property?",
                type: "boolean", required: true,
                freeTextWhen: { field: "has_guarantees_or_warranties", value: true },
                freeTextPrompt: "If yes, please give an overview." },
            { key: "has_new_build_warranty", label: "New build warranty (e.g. NHBC, Premier, LABC)?", type: "boolean", required: true,
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_damp_guarantee", label: "Damp-proofing guarantee?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_timber_guarantee", label: "Timber treatment guarantee?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_window_guarantee", label: "Guarantees for windows, doors, or double glazing?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_electrical_guarantee", label: "Electrical installation certificate?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_roofing_guarantee", label: "Roofing guarantee?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
            { key: "has_indemnity_policies", label: "Any indemnity insurance policies?", type: "boolean",
                showWhen: { field: "has_guarantees_or_warranties", value: true } },
        ],
    },
    {
        key: "occupiers",
        title: "Occupiers",
        description: "Who currently lives in the property.",
        fields: [
            { key: "is_occupied", label: "Is the property currently occupied?", type: "boolean", required: true },
            { key: "has_tenants", label: "Are there any tenants in the property?", type: "boolean", required: true,
                showWhen: { field: "is_occupied", value: true },
                freeTextWhen: { field: "has_tenants", value: true } },
            { key: "all_occupiers_will_vacate", label: "Will all occupiers vacate on or before completion?", type: "boolean",
                allowsDontKnow: true,
                showWhen: { field: "is_occupied", value: true } },
        ],
    },
    {
        key: "completion_moving",
        title: "Completion & Moving",
        description: "Practicalities of the sale — when you'd like to move and any onward purchase.",
        fields: [
            { key: "preferred_completion_date", label: "Preferred completion date", type: "date" },
            { key: "is_chain", label: "Are you in a property chain?", type: "boolean", required: true },
            { key: "has_dependent_purchase", label: "Is the sale dependent on you purchasing another property?", type: "boolean", required: true },
            { key: "dependent_purchase_status", label: "What is the status of your purchase?", type: "select",
                showWhen: { field: "has_dependent_purchase", value: true },
                options: [
                    { value: "searching", label: "Still searching" },
                    { value: "offer_made", label: "Offer made" },
                    { value: "offer_accepted", label: "Offer accepted" },
                    { value: "conveyancing", label: "Conveyancing in progress" },
                ] },
        ],
    },
    {
        key: "additional_legal",
        title: "Additional Legal Information",
        description: "Leasehold-related details.",
        leaseholdOnly: true,
        fields: [
            { key: "has_freehold_share", label: "Do you own a share of the freehold?", type: "boolean",
                freeTextWhen: { field: "has_freehold_share", value: true } },
            { key: "has_transfer_fee", label: "Is there a transfer fee or admin charge on sale?", type: "boolean",
                freeTextWhen: { field: "has_transfer_fee", value: true } },
            { key: "has_other_legal_info", label: "Any other legal information the buyer should know?", type: "boolean",
                freeTextWhen: { field: "has_other_legal_info", value: true } },
            { key: "lease_term_years", label: "How many years are remaining on the lease?", type: "integer",
                placeholder: "e.g. 99", allowsDontKnow: true,
                helpText: "If you are unsure, your conveyancer can check the title register." },
            { key: "lease_ground_rent_pcy_full_currency", label: "Annual ground rent (£)", type: "decimal",
                placeholder: "e.g. 150.00", allowsDontKnow: true,
                helpText: "Enter 0 if your ground rent is peppercorn (zero)." },
            { key: "lease_service_charge_pcy_full_currency", label: "Annual service charge (£)", type: "decimal",
                placeholder: "e.g. 1200.00", allowsDontKnow: true,
                helpText: "The amount charged for maintenance and upkeep of shared areas." },
            { key: "has_management_company", label: "Is there a management company for the building?", type: "boolean" },
            { key: "management_company_name", label: "Management company name", type: "text",
                placeholder: "e.g. Acme Property Management Ltd",
                showWhen: { field: "has_management_company", value: true } },
            { key: "management_company_contact_name", label: "Management company contact name", type: "text",
                placeholder: "e.g. Jane Smith",
                showWhen: { field: "has_management_company", value: true } },
            { key: "management_company_email", label: "Management company email address", type: "email",
                placeholder: "e.g. contact@acmepm.co.uk",
                showWhen: { field: "has_management_company", value: true } },
            { key: "management_company_phone_number", label: "Management company phone number", type: "phone",
                placeholder: "e.g. 020 7123 4567",
                showWhen: { field: "has_management_company", value: true } },
        ],
    },
];

export const shouldShowField = (
    field: TPifField,
    data: TCasePif1,
): boolean => {
    if (!field.showWhen) return true;
    const { field: dependsOn, value } = field.showWhen;
    const matches = (actual: unknown): boolean =>
        Array.isArray(value) ? value.some((v) => actual === v) : actual === value;
    if (Array.isArray(dependsOn)) {
        return dependsOn.some((f) => matches(data[f]));
    }
    return matches(data[dependsOn]);
};

const LIMITED_TITLE_AUTO_DEFAULT: Partial<Record<TPifFieldKey, boolean | string | number>> = {
    all_owners_agree: true,
    is_chain: false,
    has_dependent_purchase: false,
};

const LIMITED_TITLE_CANONICAL_TEXT: Partial<Record<TPifFieldKey, string>> = {
    preferred_completion_date: "As soon as possible",
};

const LIMITED_TITLE_AUTO_ANSWER: ReadonlyArray<TPifFieldKey> = [
    "has_commercial", "step_free_street", "step_free_throughout",
    "has_ground_rent_increase", "has_reserve_fund", "reserve_fund_included_in_service_charge",
    "has_large_expense_notice", "additional_fees_on_sale_or_letting",
    "freeholder_name", "freeholder_address", "freeholder_email",
    "managing_agent_name", "managing_agent_address", "managing_agent_email",
    "owner_required_to_be_director",
    "parking_garage", "parking_allocated", "parking_driveway", "parking_on_street",
    "parking_resident_permit", "parking_permit_cost_full_currency", "parking_metered",
    "parking_unallocated_communal", "parking_controlled_zone", "parking_none",
    "parking_other", "parking_vehicle_restriction", "has_ev_charge_point",
    "has_ev_charge_points_nearby",
    "has_disputes_or_complaints", "has_neighbour_disputes", "has_legal_action",
    "has_antisocial_behaviour", "has_noise_issues",
    "has_extensions", "extension_approved", "has_loft_conversion", "has_walls_removed",
    "has_other_structural", "has_significant_repairs", "has_change_of_use",
    "has_window_replacement", "has_conservatory_added", "has_alteration_consents_required",
    "alteration_building_regs_approval", "alteration_planning_permission",
    "alteration_listed_or_conservation_consent", "alteration_deed_restriction_consent",
    "alteration_works_unfinished", "has_planning_breach", "has_unresolved_building_control",
    "will_pay_for_indemnity_for_breach",
    "has_received_notices", "has_planning_notices", "has_building_control_notices",
    "has_environmental_notices", "has_notice_neighbour_development",
    "has_notice_planning_application", "has_notice_required_maintenance",
    "has_notice_listed_building_application", "has_notice_infrastructure_project",
    "has_notice_party_wall", "has_other_notices",
    "has_specialist_issues", "has_japanese_knotweed", "has_flooding", "has_subsidence",
    "has_asbestos", "has_radon", "has_coastal_erosion", "has_mining", "has_listed_building",
    "has_conservation_area", "has_tree_preservation_order", "tree_work_consents_obtained",
    "has_authority_notified_safety",
    "is_standard_construction", "has_flat_roof", "flat_roof_percentage",
    "flat_roof_last_renewed_year", "has_building_safety_remediation_required",
    "has_energy_retrofit", "has_spray_foam", "is_above_two_storeys", "building_storey_count",
    "property_storey_location", "is_mixed_use_building",
    "has_altered_for_council_tax_band",
    "electricity_supplier", "gas_connected", "gas_supplier", "lpg_connected", "lpg_supplier",
    "oil_connected", "oil_supplier", "has_cable_tv_satellite", "has_sustainable_drainage_system",
    "water_supplier", "water_meter", "sewerage_connection", "sewerage_other",
    "has_septic_tank", "septic_tank_last_emptied_date", "has_cesspit", "cesspit_last_emptied_date",
    "has_sewage_treatment_plant", "sewage_treatment_plant_install_date",
    "sewage_treatment_plant_last_serviced_date", "sewage_treatment_plant_building_regs_approved",
    "off_mains_other_properties_connect", "off_mains_off_site",
    "off_mains_environment_agency_registered", "off_mains_drains_into_waterway",
    "off_mains_complies_with_general_binding_rules", "has_utility_licences_or_maintenance_agreements",
    "heating_type", "heating_other", "boiler_age", "heating_install_date",
    "heating_last_serviced_date", "heating_in_good_working_order", "broadband_type",
    "mobile_signal", "solar_panels", "in_ulez",
    "is_property_insured", "buildings_insurance_provider", "insurance_policy_number",
    "has_insurance_claims", "has_insurance_refused", "has_title_indemnity",
    "boundary_front", "boundary_rear", "boundary_left", "boundary_right",
    "has_boundary_issues", "has_boundary_disputes", "boundary_agreements",
    "has_part_outside_legal_ownership", "has_part_on_separate_title",
    "has_rights_of_way", "has_shared_access", "has_informal_arrangements", "has_easements",
    "has_access_blocked_or_charged", "has_other_rights_or_arrangements", "has_public_right_of_way",
    "has_rights_of_light", "has_rights_through_custom", "has_mines_and_minerals",
    "has_chancel_liability",
    "has_environmental_issues", "has_contamination", "has_trees", "has_pest_issues",
    "has_damp_issues",
    "has_crime_or_death", "has_failed_transactions",
    "has_restrictions", "has_charges",
    "boundaries_match_title", "has_boundary_changes",
    "has_services_crossing", "has_neighbour_services", "has_shared_drains",
    "has_green_deal",
    "has_guarantees_or_warranties", "has_new_build_warranty", "has_damp_guarantee",
    "has_timber_guarantee", "has_window_guarantee", "has_electrical_guarantee",
    "has_roofing_guarantee", "has_indemnity_policies",
    "preferred_completion_date", "dependent_purchase_status",
    "has_freehold_share", "has_transfer_fee", "has_other_legal_info", "has_management_company",
    "management_company_name", "management_company_contact_name", "management_company_email",
    "management_company_phone_number",
];

const LIMITED_TITLE_AUTO_ANSWER_SET: ReadonlySet<TPifFieldKey> = new Set(LIMITED_TITLE_AUTO_ANSWER);

export const limitedTitleBehaviorOf = (field: TPifField): TPifLimitedTitleBehavior => {
    const def = LIMITED_TITLE_AUTO_DEFAULT[field.key];
    if (def !== undefined) return { kind: "auto_default", value: def };
    if (LIMITED_TITLE_AUTO_ANSWER_SET.has(field.key)) {
        const canonicalText = LIMITED_TITLE_CANONICAL_TEXT[field.key];
        return canonicalText !== undefined ? { kind: "auto_answer", canonicalText } : { kind: "auto_answer" };
    }
    return { kind: "show" };
};

export const shouldShowFieldForTitleMode = (
    field: TPifField,
    data: TCasePif1,
    titleMode: TPifTitleMode,
): boolean => {
    if (!shouldShowField(field, data)) return false;
    if (titleMode === "full") return true;
    return limitedTitleBehaviorOf(field).kind === "show";
};

export const isLimitedTitleOptionalField = (
    field: TPifField,
    data: TCasePif1,
): boolean =>
    shouldShowField(field, data) && limitedTitleBehaviorOf(field).kind === "auto_answer";

export const pifAutoAnswerFields = (): TPifField[] =>
    pifSections.flatMap((s) => s.fields).filter((f) => limitedTitleBehaviorOf(f).kind === "auto_answer");

export const pifAutoDefaultFields = (): { field: TPifField, value: boolean | string | number }[] =>
    pifSections.flatMap((s) => s.fields).flatMap((f) => {
        const b = limitedTitleBehaviorOf(f);
        return b.kind === "auto_default" ? [{ field: f, value: b.value }] : [];
    });

export const pifNotKnownText = (
    field: TPifField,
    titleMode: TPifTitleMode = "limited",
): string => {
    const b = limitedTitleBehaviorOf(field);
    if (b.kind === "auto_answer" && b.canonicalText !== undefined) {
        return b.canonicalText;
    }
    return titleMode === "limited"
        ? "Not known — sold by personal representative, no personal knowledge"
        : "Not known";
};

export const fieldAllowsDontKnow = (field: TPifField): boolean =>
    field.allowsDontKnow === true;

export const pifDontKnowEligibleFieldKeys = (): TPifFieldKey[] =>
    pifSections.flatMap((s) => s.fields).filter(fieldAllowsDontKnow).map((f) => f.key);

export const resolvePifDontKnowFieldKey = (key: string): TPifFieldKey | undefined =>
    pifDontKnowEligibleFieldKeys().find((k) => k === key);

const pifNotKnownColumnKeys = (): TPifFieldKey[] => [
    ...pifAutoAnswerFields().map((f) => f.key),
    ...pifDontKnowEligibleFieldKeys(),
];

export const resolvePifNotKnownColumn = (key: string): TPifFieldKey | undefined =>
    pifNotKnownColumnKeys().find((k) => k === key);

export const shouldShowSection = (section: TPifSection, includeLeasehold: boolean): boolean =>
    section.leaseholdOnly === true ? includeLeasehold : true;

export type TPifMissingField = {
    section_key: string,
    section_title: string,
    field_key: TPifFieldKey,
    field_label: string,
};

export const isPifFieldValidFormat = (field: TPifField, value: unknown): boolean => {
    if (value === null || value === undefined || value === "") return true;
    if (field.type === "email") {
        return typeof value === "string" && new RegExp(regexes.constants.EMAIL_REGEX).test(value);
    }
    if (field.type === "phone") {
        return typeof value === "string" && E164PhoneNumber.toValidE164OrNull(value) !== null;
    }
    return true;
};

export const isFieldAnswered = (field: TPifField, value: unknown): boolean => {
    if (field.type === "boolean") return value === true || value === false;
    if (field.type === "integer") return typeof value === "number";
    if (field.type === "decimal") return typeof value === "number";
    if (field.type === "email") return typeof value === "string" && new RegExp(regexes.constants.EMAIL_REGEX).test(value);
    if (field.type === "phone") return typeof value === "string" && E164PhoneNumber.toValidE164OrNull(value) !== null;
    return typeof value === "string" && value.length > 0;
};

// Everything that stops a seller submitting: a required answer that is missing,
// and an optional contact answer in the wrong format.
//
// `dontKnowKeys` is the cases_pif__auto_answered marker set. A field the seller
// explicitly answered "I don't know" is answered, not missing — both states are
// null in the column, so the marker is the only thing that tells them apart.
// This MUST be threaded through every caller: the portal and the server-side
// sign gate both validate independently, and if only one of them knows about
// the markers the seller passes one and is rejected by the other with no
// explanation.
export const getInvalidPifFields = (
    data: TCasePif1,
    includeLeasehold: boolean,
    titleMode: TPifTitleMode = "full",
    dontKnowKeys: ReadonlyArray<string> = [],
): TPifMissingField[] => {
    const invalid: TPifMissingField[] = [];
    for (const section of pifSections) {
        if (!shouldShowSection(section, includeLeasehold)) continue;
        for (const field of section.fields) {
            if (!shouldShowFieldForTitleMode(field, data, titleMode)) continue;
            if (fieldAllowsDontKnow(field) && dontKnowKeys.includes(field.key)) continue;
            const value = data[field.key];
            if (field.required) {
                if (!isFieldAnswered(field, value)) {
                    invalid.push({
                        section_key: section.key,
                        section_title: section.title,
                        field_key: field.key,
                        field_label: field.label,
                    });
                }
            } else if (value !== null && value !== undefined && value !== "") {
                if (!isPifFieldValidFormat(field, value)) {
                    invalid.push({
                        section_key: section.key,
                        section_title: section.title,
                        field_key: field.key,
                        field_label: field.label,
                    });
                }
            }
        }
    }
    return invalid;
};
